"use client";
import {
  Assistant,
  Book,
  FolderCopy,
  Help,
  QuestionAnswer,
  Settings,
  Summarize,
  Timeline,
} from "@mui/icons-material";
import { Chip } from "@mui/material";
import { createTheme as createMuiTheme, Theme } from "@mui/material/styles";
import * as React from "react";
import { useSessionStorage } from "../../hooks/useStorage";
import { DialogsProvider } from "../useDialogs";
import { Navigation } from "../useNavigation/navigation";
import { NavigationContext } from "../useNavigation/useNavigation";
import { NotificationsProvider } from "../useNotifications";
import { Session, SessionContext } from "../useSession/useSession";
import { AppThemeProvider } from "./AppThemeProvider";

export interface AppProviderProps {
  /**
   * The content of the app provider.
   */
  children: React.ReactNode;
  /**
   * A [CSS variables theme](https://mui.com/material-ui/customization/css-theme-variables/overview/) is recommended.
   * @default createTheme()
   */
  theme?: Theme;
}

function createTheme(): Theme {
  return createMuiTheme({
    cssVariables: {
      colorSchemeSelector: "data-toolpad-color-scheme",
    },
    colorSchemes: { dark: true },
  });
}

function AppProvider(props: AppProviderProps) {
  const { children, theme = createTheme() } = props;
  const [isExpanded, setIsExpanded] = React.useState<boolean>(true);
  const [session, setSession, removeSession] = useSessionStorage<
    Session | undefined
  >("session", undefined);

  function getNavigation() {
    let navigation = [];
    if ((session?.OrionUserGroups?.length ?? 0) > 0) {
      navigation.push(
        {
          kind: "header",
          title: "Orion",
        },
        {
          kind: "divider",
        },
        {
          segment: "orion",
          title: "Documents",
          icon: <Summarize style={{ color: "black" }} />,
          children: [
            {
              segment: "assets",
              title: "Dossiers",
              icon: <FolderCopy style={{ color: "black" }} />,
            },
            {
              segment: "configs",
              title: "Configurations",
              icon: <Settings style={{ color: "black" }} />,
            },
            {
              segment: "workflows",
              title: "Flux",
              icon: <Timeline style={{ color: "black" }} />,
              action: (
                <Chip label="Coming Soon" color="secondary" size="small" />
              ),
            },
            {
              segment: "api",
              title: "Documentation",
              icon: <Help style={{ color: "black" }} />,
              action: (
                <Chip label="Coming Soon" color="secondary" size="small" />
              ),
            },
          ],
        }
      );
    }
    if ((session?.SiriusUserGroups?.length ?? 0) > 0) {
      navigation.push(
        {
          kind: "header",
          title: "Sirius",
        },
        {
          kind: "divider",
        },
        {
          segment: "sirius",
          title: "Assistants Virtuels",
          icon: <Assistant style={{ color: "black" }} />,
          children: [
            {
              segment: "bots",
              title: "Agents",
              icon: <QuestionAnswer style={{ color: "black" }} />,
            },
            {
              segment: "connectors",
              title: "Ma documentation",
              icon: <Book style={{ color: "black" }} />,
              action: (
                <Chip label="Coming Soon" color="secondary" size="small" />
              ),
            },
          ],
        }
      );
    }
    return navigation as Navigation;
  }

  return (
    <SessionContext.Provider value={{ session, setSession, removeSession }}>
      <AppThemeProvider theme={theme}>
        <NotificationsProvider>
          <DialogsProvider>
            <NavigationContext.Provider
              value={{ navigation: getNavigation(), isExpanded, setIsExpanded }}
            >
              {children}
            </NavigationContext.Provider>
          </DialogsProvider>
        </NotificationsProvider>
      </AppThemeProvider>
    </SessionContext.Provider>
  );
}

export { AppProvider };
